import {
  Baseline,
  IElement,
  OneOfElementListChildren,
  OneOfElements,
  Project,
} from '@shared/models/project.model';
import { ProposalComputeRecord } from '@shared/models/pipeline/compute.model';
import type { Results } from '@shared/models/unit.model';
import {
  ResultsRecord,
  SbefGroupResultsRecord,
} from '@shared/models/unit.model';
import {
  compressConversionFactors,
  sumConversionFactors,
} from '@shared/helpers/conversion-factors.helpers';
import { getBuildingLevelResults } from '@shared/helpers/results/building-level-results.helpers';
import {
  buildSbefGroupBuckets,
  getElementSbefGroupKey,
  setSbefGroupResults,
} from '@shared/helpers/sbef-results.helpers';
import {
  SbefScopeGroupKey,
  sbefScopeGroupKeys,
} from '@shared/construction/sbef-custom-groups.data';
import { SITE_OPERATIONS_SBEF_GROUP_KEY } from '@shared/helpers/sbef-grouping.helpers';
import type { IProposal } from '@shared/models/proposals.model';
import {
  applyOwnContentCoverage,
  mapProductElementResults,
} from '../results.helpers';
import {
  getProposalById,
  isElementActiveInProposal,
} from '../proposal.helpers';
import {
  type ElementPath,
  getChildElements,
  getElementById,
  getPathAndElementById,
  getPathToElement,
  isBuildingVersionElement,
  isElement,
} from '../recursive-element.helpers';

import { guards } from '../typeguards.helpers';
import { isTemporaryElementId } from '../element.helpers';
import { omitTemporaryElementFromResultsRecord } from '../results.helpers';
import { projectGuards } from '../guards/project-guards.helpers';
import {
  buildElementPathIndex,
  getElementByPath,
  type RelativePathIndexMap,
} from './batch-update.helpers';
import { getVisibleChildren } from './visible-children.helpers';

/**
 * Build the {@link ElementPath} for a leaf element using the pre-computed path index.
 * The path is `[version, ...ancestor IElements, target]` so that the category mapper
 * can walk ancestors via `path.toReversed()` to find owning categories.
 * Returns `undefined` when the element is not in the index.
 */
const tryBuildElementPath = (
  id: string,
  target: OneOfElements,
  resolvedVersion: Baseline,
  pathIndex: RelativePathIndexMap,
): ElementPath | undefined => {
  const indices = pathIndex.get(id);
  if (indices === undefined) return undefined;
  const path: ElementPath = [resolvedVersion];
  for (let depth = 1; depth < indices.length; depth++) {
    const node = getElementByPath(resolvedVersion, indices.slice(0, depth));
    if (!node) break;
    if (projectGuards.is.element(node)) path.push(node);
  }
  return [...path, target] as unknown as ElementPath;
};

/**
 * Post-order traversal (deepest descendants first, then parents) so each node can
 * aggregate {@link Results} from its children before the parent row is written.
 */
const collectPostOrder = (
  version: Baseline,
  out: OneOfElements[] = [],
): OneOfElements[] => {
  const visit = (el: OneOfElements) => {
    for (const c of getChildElements(el)) visit(c);
    out.push(el);
  };
  for (const el of version.elements ?? []) visit(el);
  return out;
};

/**
 * Applies compute-time structural enrichment to the version tree before result rollups.
 * Proposal results can add category children that are not written to the baseline tree.
 */
const applyEnrichedElementsToVersion = (
  version: Baseline,
  enrichedElements: ProposalComputeRecord['enrichedElements'],
): Baseline => {
  if (!enrichedElements) return version;

  const applyEnrichedElement = (
    element: OneOfElementListChildren,
  ): OneOfElementListChildren => {
    const enriched = projectGuards.is.element(element)
      ? enrichedElements[element.id]
      : undefined;
    const source = enriched ?? element;

    if (!projectGuards.is.element(source)) return source;

    return {
      ...source,
      elements: source.elements.map(applyEnrichedElement),
    };
  };

  return {
    ...version,
    elements: version.elements.map(applyEnrichedElement),
  };
};

/**
 * Fallback path resolution for orphan compute ids that are not in the version tree's
 * {@link buildElementPathIndex}. Tries {@link getPathToElement}, {@link getPathAndElementById},
 * and finally looks inside {@link ProposalComputeRecord.enrichedElements}.
 */
const resolveOrphanProductPath = (
  id: string,
  resolvedVersion: Baseline,
  enrichedElements: ProposalComputeRecord['enrichedElements'],
): { path: ElementPath; element: OneOfElements } | undefined => {
  let path: ElementPath | undefined = getPathToElement(
    resolvedVersion,
    id,
    false,
  ) as ElementPath | undefined;
  let element = path?.length
    ? getElementById(resolvedVersion, id, false)
    : undefined;
  if (path?.length && element)
    path = [...path, element] as unknown as ElementPath;

  if ((!path?.length || !element) && id) {
    const found = getPathAndElementById(resolvedVersion, id, false);
    if (found.path?.length && found.element) {
      path = [...found.path, found.element] as unknown as ElementPath;
      element = found.element;
    }
  }

  if ((!path?.length || !element) && id && enrichedElements) {
    for (const enriched of Object.values(enrichedElements)) {
      const childInEnriched = enriched.elements?.find((e) => e.id === id);
      if (!childInEnriched || !projectGuards.is.productElement(childInEnriched))
        continue;
      const pathToParent = getPathToElement(
        resolvedVersion,
        enriched.id,
        false,
      );
      if (!pathToParent?.length) continue;
      const parentInVersion = pathToParent[pathToParent.length - 1];
      const childInVersion = getChildElements(parentInVersion).find(
        (e) => e.id === id,
      );
      if (childInVersion && projectGuards.is.productElement(childInVersion)) {
        return {
          path: [...pathToParent, childInVersion] as unknown as ElementPath,
          element: childInVersion,
        };
      }
    }
  }

  if (element && path?.length) return { path, element };
  return undefined;
};

/**
 * Apply a node's own {@link ElementPropertyName.ContentCoverage} to each SBEF group bucket,
 * mirroring how the flat total is scaled. Iterates the known scope group keys so the result
 * stays keyed by {@link SbefScopeGroupKey}.
 */
const scaleSbefGroupBuckets = (
  buckets: SbefGroupResultsRecord,
  node: IElement | Baseline,
): SbefGroupResultsRecord => {
  const scaled: SbefGroupResultsRecord = {};
  for (const key of sbefScopeGroupKeys) {
    const value = buckets[key];
    if (!value) continue;
    scaled[key] = applyOwnContentCoverage(value, node);
  }
  return scaled;
};

/**
 * Sum the visible children's stored results and apply this element's own
 * {@link ElementPropertyName.ContentCoverage} (not inherited). Category mapping
 * has already been applied at the leaf product level by
 * {@link mapProductElementResults}, so the parent rollup is just a sum + coverage.
 *
 * @param applyCoverage - False without project context, where coverage is unresolved.
 */
const rollupParentResults = (
  element: OneOfElements,
  visibleChildren: OneOfElements[],
  record: ResultsRecord,
  { applyCoverage }: { applyCoverage: boolean },
): Results => {
  const childResults = visibleChildren
    .map((c) => record[c.id])
    .filter(guards.is.defined);
  const summed = sumConversionFactors(...childResults);
  // SBEF breakdown: children grouped by their own SBEF group (product children fold
  // into this element's group). Scaled by the same own ContentCoverage as the flat total.
  const buckets = buildSbefGroupBuckets(
    getElementSbefGroupKey(element),
    visibleChildren,
    record,
  );
  const ownsCoverage = isElement(element) || isBuildingVersionElement(element);
  if (!applyCoverage || !ownsCoverage) {
    return setSbefGroupResults(compressConversionFactors(summed), buckets);
  }
  return setSbefGroupResults(
    compressConversionFactors(applyOwnContentCoverage(summed, element)),
    scaleSbefGroupBuckets(buckets, element),
  );
};

/** Everything needed to write one element row during the post-order walk. */
type ElementRowContext = {
  items: ProposalComputeRecord['items'];
  enrichedElements: ProposalComputeRecord['enrichedElements'];
  record: ResultsRecord;
  resolvedVersion: Baseline;
  pathIndex: RelativePathIndexMap;
  proposal: IProposal | undefined;
  project: Project | undefined;
};

/**
 * Writes `record[element.id]` for one element in the post-order walk, choosing between
 * the two ways a row comes to exist:
 *
 * 1. **Parent** (any visible child already has a row): sum children + own coverage.
 * 2. **Leaf** (own compute results): product elements run category + lifetime mapping
 *    when there is project context; anything else stores its compute results as-is.
 *
 * Elements with neither children nor own results get no row at all.
 */
const writeElementRow = (
  element: OneOfElements,
  {
    items,
    record,
    resolvedVersion,
    pathIndex,
    proposal,
    project,
  }: ElementRowContext,
): void => {
  const id = element.id;
  const elementPath =
    tryBuildElementPath(id, element, resolvedVersion, pathIndex) ??
    ([resolvedVersion, element] as unknown as ElementPath);
  const visibleChildren = getVisibleChildren(element, elementPath, proposal);

  if (visibleChildren.some((child) => record[child.id] !== undefined)) {
    record[id] = rollupParentResults(element, visibleChildren, record, {
      applyCoverage: project !== undefined,
    });
    return;
  }

  const item = items[id];
  if (!item || Object.keys(item.results).length === 0) return;

  record[id] =
    project && projectGuards.is.productElement(element)
      ? mapProductElementResults(item.results, {
          project,
          path: elementPath,
          productElement: element,
        })
      : item.results;
};

/**
 * Writes rows for compute ids that are not in the version tree (e.g. products only
 * present in {@link ProposalComputeRecord.enrichedElements}), so their results are not
 * lost. Product elements whose path can be resolved still get category + lifetime mapping.
 */
const writeOrphanRows = (
  idsInVersionTree: Set<string>,
  {
    items,
    enrichedElements,
    record,
    resolvedVersion,
    project,
  }: ElementRowContext,
): void => {
  for (const id of Object.keys(items)) {
    if (idsInVersionTree.has(id)) continue;
    if (isTemporaryElementId(id)) continue;
    const item = items[id];
    if (!item || Object.keys(item.results).length === 0) continue;

    const resolved = project
      ? resolveOrphanProductPath(id, resolvedVersion, enrichedElements)
      : undefined;

    record[id] =
      project && resolved && projectGuards.is.productElement(resolved.element)
        ? mapProductElementResults(item.results, {
            project,
            path: resolved.path,
            productElement: resolved.element,
          })
        : item.results;
  }
};

/**
 * Fold {@link buildingLevelResults} into the Site operations bucket (group 9) so the SBEF
 * breakdown still sums back to the flat version total.
 */
const addBuildingLevelToSiteOperationsBucket = (
  buckets: SbefGroupResultsRecord,
  buildingLevelResults: Results,
): SbefGroupResultsRecord => {
  if (Object.keys(buildingLevelResults).length === 0) return buckets;
  return {
    ...buckets,
    [SITE_OPERATIONS_SBEF_GROUP_KEY]: sumConversionFactors(
      buckets[SITE_OPERATIONS_SBEF_GROUP_KEY] ?? {},
      buildingLevelResults,
    ),
  };
};

/**
 * Builds the version-level row: the top-level elements selected in this proposal, scaled
 * by the version's own ContentCoverage, plus the {@link getBuildingLevelResults} that no
 * element owns. Top-level children already carry their category/coverage adjustments, so
 * the element part is a pure sum.
 *
 * The BD7/BD8 gating deliberately reads the **unscaled** buckets: it asks whether
 * resources were modelled at all, which content coverage does not change.
 */
const buildVersionRow = (
  resolvedVersion: Baseline,
  record: ResultsRecord,
  proposal: IProposal | undefined,
  project: Project | undefined,
): Results => {
  const topLevelInProposal = getChildElements(resolvedVersion).filter((el) =>
    isElementActiveInProposal(proposal, [], el),
  );
  const topLevelResults = topLevelInProposal
    .map((el) => record[el.id])
    .filter(guards.is.defined);
  const versionTotal =
    topLevelResults.length > 0 ? sumConversionFactors(...topLevelResults) : {};
  const versionBuckets = buildSbefGroupBuckets(
    undefined,
    topLevelInProposal,
    record,
  );
  const buildingLevelResults = project
    ? getBuildingLevelResults(project, versionBuckets)
    : {};

  return setSbefGroupResults(
    compressConversionFactors(
      sumConversionFactors(
        applyOwnContentCoverage(versionTotal, resolvedVersion),
        buildingLevelResults,
      ),
    ),
    addBuildingLevelToSiteOperationsBucket(
      scaleSbefGroupBuckets(versionBuckets, resolvedVersion),
      buildingLevelResults,
    ),
  );
};

/**
 * Build a {@link ResultsRecord} from a proposal's compute record.
 * Used by run-pipeline to set `proposal.resultsRecord` from compute output.
 *
 * Three steps, in order:
 *
 * 1. {@link writeElementRow} for every element in **post-order** (deepest first), so a
 *    parent can sum children that already have rows.
 * 2. {@link writeOrphanRows} for compute ids outside the version tree.
 * 3. {@link buildVersionRow} for the version total, including the building-level results
 *    ({@link getBuildingLevelResults}) that belong to no element.
 *
 * Deactivated or deleted elements are excluded from their parent's rollup, but their own
 * `record[id]` slot is **preserved** so callers can show what the element would contribute
 * if re-activated (e.g. greyed-out rows in UI).
 *
 * @param computeRecord - Proposal compute record (items id → results)
 * @param version - Building version (for version.id and tree rollups)
 * @param project - Optional; when set, lifetime/category/coverage mappers are applied
 * @param pathIndex - Optional id→indices map from {@link buildElementPathIndex}
 * @returns ResultsRecord including per-element rollups and version-level total
 */
export const buildResultsRecordFromProposalComputeRecord = (
  computeRecord: ProposalComputeRecord,
  version: Baseline,
  project?: Project,
  pathIndex?: RelativePathIndexMap,
): ResultsRecord => {
  const items = computeRecord.items;
  const record: ResultsRecord = {};
  const enrichedElements = computeRecord.enrichedElements;

  // Always use the passed version for tree traversal. When called for a proposal,
  // `version` is the resolved version (baseline + proposal actions applied), which
  // carries the correct isDeactivated state. Overriding with the project's baseline
  // would hide proposal-specific deactivations and let deactivated elements contribute
  // to parent CO2 rollups.
  const resolvedVersion = applyEnrichedElementsToVersion(
    version,
    enrichedElements,
  );
  const proposal = getProposalById(
    resolvedVersion,
    computeRecord.proposalId,
    false,
  );

  const resolvedPathIndex = enrichedElements
    ? buildElementPathIndex(resolvedVersion)
    : (pathIndex ?? buildElementPathIndex(resolvedVersion));

  const postOrder = collectPostOrder(resolvedVersion);
  const idsInVersionTree = new Set(postOrder.map((e) => e.id));
  const context: ElementRowContext = {
    items,
    enrichedElements,
    record,
    resolvedVersion,
    pathIndex: resolvedPathIndex,
    proposal,
    project,
  };

  // Bottom-up: product leaves apply categories + lifetime; parents sum + coverage
  for (const element of postOrder) {
    if (isTemporaryElementId(element.id)) continue;
    writeElementRow(element, context);
  }

  writeOrphanRows(idsInVersionTree, context);

  record[resolvedVersion.id] = buildVersionRow(
    resolvedVersion,
    record,
    proposal,
    project,
  );

  return omitTemporaryElementFromResultsRecord(record);
};

/**
 * Re-derive the per-SBEF-group breakdown for an existing flat {@link ResultsRecord} without
 * re-running the compute pipeline. Walks the version tree post-order, attaching group buckets
 * (scaled by each node's own ContentCoverage, mirroring {@link rollupParentResults}) to every
 * parent row and the version-level row. Used by the SBEF backfill migration so existing
 * `resultsRecord`s on building versions and proposals gain the breakdown without enrich.
 * @param version - The (resolved) building version whose tree describes the grouping.
 * @param record - The stored flat results record to augment.
 */
export const deriveSbefGroupResultsRecord = (
  version: Baseline,
  record: ResultsRecord,
): ResultsRecord => {
  const out: ResultsRecord = { ...record };

  const attach = (
    node: IElement | Baseline,
    children: OneOfElements[],
    ownGroupKey: SbefScopeGroupKey | undefined,
  ): void => {
    const row = out[node.id];
    if (!row) return;
    const buckets = buildSbefGroupBuckets(ownGroupKey, children, out);
    out[node.id] = setSbefGroupResults(
      row,
      scaleSbefGroupBuckets(buckets, node),
    );
  };

  const visit = (element: OneOfElements): void => {
    const children = getChildElements(element);
    for (const child of children) visit(child);
    if (
      children.length &&
      (isElement(element) || isBuildingVersionElement(element))
    ) {
      attach(element, children, getElementSbefGroupKey(element));
    }
  };

  for (const element of getChildElements(version)) visit(element);
  attach(version, getChildElements(version), undefined);

  return out;
};
